import { useNavigate } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import type { RegionCount } from '@/types/statistics'

/** 17개 시도 순서 (북→남) */
const REGION_ORDER = [
  '서울특별시', '경기도', '인천광역시',
  '강원특별자치도', '충청북도', '충청남도',
  '대전광역시', '세종특별자치시', '전북특별자치도',
  '전라남도', '광주광역시', '경상북도',
  '대구광역시', '경상남도', '울산광역시',
  '부산광역시', '제주특별자치도',
]

/** 짧은 이름 */
const SHORT_NAMES: Record<string, string> = {
  '서울특별시': '서울',
  '경기도': '경기',
  '인천광역시': '인천',
  '강원특별자치도': '강원',
  '충청북도': '충북',
  '충청남도': '충남',
  '대전광역시': '대전',
  '세종특별자치시': '세종',
  '전북특별자치도': '전북',
  '전라남도': '전남',
  '광주광역시': '광주',
  '경상북도': '경북',
  '대구광역시': '대구',
  '경상남도': '경남',
  '울산광역시': '울산',
  '부산광역시': '부산',
  '제주특별자치도': '제주',
}

/** 밀도에 따른 색상 */
function densityColor(count: number, maxCount: number) {
  const ratio = maxCount > 0 ? count / maxCount : 0
  if (ratio > 0.7) return 'bg-blue-600 text-white'
  if (ratio > 0.4) return 'bg-blue-400 text-white'
  if (ratio > 0.2) return 'bg-blue-200 text-blue-900'
  return 'bg-blue-100 text-blue-800'
}

interface Props {
  data: RegionCount[]
}

export default function RegionGrid({ data }: Props) {
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })

  const countMap = new Map(data.map((d) => [d.sido, d.count]))
  const maxCount = Math.max(...data.map((d) => d.count), 1)

  return (
    <div ref={ref} className="grid grid-cols-4 gap-2">
      {REGION_ORDER.map((sido, i) => {
        const count = countMap.get(sido) ?? 0
        const shortName = SHORT_NAMES[sido] ?? sido
        const color = densityColor(count, maxCount)

        return (
          <motion.button
            key={sido}
            onClick={() => navigate(`/facilities?sido=${encodeURIComponent(sido)}`)}
            className={`rounded-xl p-2.5 text-center transition-shadow hover:shadow-md ${color}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, delay: i * 0.03 }}
          >
            <p className="text-xs font-bold">{shortName}</p>
            <p className="text-[11px] font-semibold mt-0.5">
              {isInView ? count.toLocaleString() : '0'}
            </p>
          </motion.button>
        )
      })}
    </div>
  )
}
